import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import './hp-cart-reference.js';
import './hp-cart-upsell-position.js';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;
  #previousScrollRestoration = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('cancel', this.#handleNativeCancel, true);

    if (history.state?.cartDrawerOpen) {
      const restoration = history.state.cartDrawerScrollRestoration;
      if (restoration === 'auto' || restoration === 'manual') history.scrollRestoration = restoration;
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('cancel', this.#handleNativeCancel, true);
    this.#historyAbortController?.abort();
    this.#restoreScrollRestoration();
  }

  #handleNativeCancel = (event) => {
    if (event.target !== this.refs.dialog) return;
    event.preventDefault();
    this.closeDialog();
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    this.#previousScrollRestoration ??= history.scrollRestoration;
    history.scrollRestoration = 'manual';

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true, cartDrawerScrollRestoration: this.#previousScrollRestoration }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      window.addEventListener('popstate', this.#restoreScrollRestoration, { once: true });
      history.back();
    } else {
      this.#restoreScrollRestoration();
    }
  };

  #restoreScrollRestoration = () => {
    if (this.#previousScrollRestoration === null) return;
    window.setTimeout(() => {
      if (this.#previousScrollRestoration === null || this.refs.dialog?.open || history.state?.cartDrawerOpen) return;
      history.scrollRestoration = this.#previousScrollRestoration;
      this.#previousScrollRestoration = null;
    }, 0);
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    if (['hp-buy-now', 'hp-checkout-upsell'].includes(event.detail.data?.source)) return;
    if ((this.hasAttribute('auto-open') || this.closest('.hp-cart-drawer-bridge')) && !event.detail.data?.didError) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// Keep the premium HeartyPaws cart's product viewport modestly shorter so
// totals and checkout remain visible while larger carts scroll independently.
if (!document.getElementById('hp-cart-product-window-height')) {
  const compactProductWindowStyles = document.createElement('style');
  compactProductWindowStyles.id = 'hp-cart-product-window-height';
  compactProductWindowStyles.textContent = `
    .hp-cart-drawer-bridge .cart-drawer__items {
      max-height: clamp(245px, 36dvh, 340px) !important;
      overflow-y: auto !important;
      overscroll-behavior: contain;
      scrollbar-width: thin;
      scrollbar-color: rgba(107, 53, 24, .24) transparent;
    }

    @media (max-width: 749px) {
      .hp-cart-drawer-bridge .cart-drawer__items {
        max-height: clamp(210px, 33dvh, 300px) !important;
      }
    }
  `;
  document.head.append(compactProductWindowStyles);
}
